import { FC, SyntheticEvent, useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import CompareArrowsIcon from '@mui/icons-material/CompareArrows'
import { Box, Button, Typography } from '@mui/material'

import { useRoleCheck } from '../../features/account/hooks/roleHooks'
import { RoleEnum } from '../../features/account/types/RoleEnum'
import {
  CompareTargetPicker,
  CreativeAdsTable,
  GameCreativeTagsTable,
  GameMotivationDetailView,
  GameOverviewTab,
  GamePageBodySkeleton,
  GamePageIdentity,
  GameStoryPatternsTab,
  MotivationDiagnosis,
  MotivationsPanel,
  SectionCard,
  ShowcaseBreadcrumb,
  UAInsightsDialogs,
  UAInsightsErrorState,
  UAInsightsNavigationProvider,
  UAInsightsPageHeader,
  useGameCreativeDetails,
  useGamePageNavigation,
  useScrollToTopOnChange,
  useStickyHeader,
  useUAInsightsActions,
  useUAInsightsMeta,
  useUATaxonomy,
} from '../../features/ua-insights'
import type { ShowcaseCrumb } from '../../features/ua-insights'
import { useDocumentTitle } from '../../hooks/useDocumentTitle'
import usePage from '../../hooks/usePage'
import PageService from '../../services/PageService'
import { DEFAULT_TAB, GamePageTabs, TabId, isTabId } from './GamePageTabs'

const UAInsightsGamePage: FC = () => {
  const { t } = useTranslation('ua-insights')
  // Register the page with the global header / analytics so reloading the
  // route shows the correct "UA Insights" title instead of falling back to
  // the default "Daily Insights" page.
  usePage(PageService.getPageWithId('ua-insights-game'), 'Visited UA Insights Game')
  useDocumentTitle(t('page_title'))
  const hasAccess = useRoleCheck(RoleEnum.ua_insights)

  if (!hasAccess) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6">{t('no_access')}</Typography>
      </Box>
    )
  }

  return (
    <UAInsightsNavigationProvider>
      <UAInsightsGamePageContent />
    </UAInsightsNavigationProvider>
  )
}

const UAInsightsGamePageContent: FC = () => {
  const { t } = useTranslation('ua-insights')
  const { gameId: gameIdParam } = useParams<{ gameId: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { openCompareGame, openCompareGenreResult } = useUAInsightsActions()
  // The in-page Motivation drill-down + its hand-offs (back to Motivations,
  // drill into a story pattern) follow the shared game-page navigation contract.
  const { activeMotivationId, openMotivation: openMotivationView, backToMotivations, openPatternFromMotivation } = useGamePageNavigation()
  const { motivationById } = useUAInsightsMeta()
  const { motivationLabel } = useUATaxonomy()
  const activeMotivation = activeMotivationId ? motivationById(activeMotivationId) : undefined

  const tabParam = searchParams.get('tab')
  const activeTab: TabId = isTabId(tabParam) ? tabParam : DEFAULT_TAB

  const motivationCrumbs = useMemo<ShowcaseCrumb[]>(
    () => [{ label: t('tab_motivations'), onClick: backToMotivations }, { label: activeMotivation ? motivationLabel(activeMotivation) : '' }],
    [t, backToMotivations, activeMotivation, motivationLabel]
  )

  const handleTabChange = useCallback(
    (_event: SyntheticEvent, nextTab: TabId) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (nextTab === DEFAULT_TAB) {
            next.delete('tab')
          } else {
            next.set('tab', nextTab)
          }
          return next
        },
        { replace: false }
      )
    },
    [setSearchParams]
  )

  // `activeMotivationId` is part of the key so opening/closing the in-page
  // Motivation view (which doesn't change `activeTab`) also resets the scroll.
  useScrollToTopOnChange(`${activeTab}:${activeMotivationId ?? ''}`)

  const isCompactHeader = useStickyHeader()

  // Anchor + open state for the Compare picker popover. The Compare button
  // no longer goes straight to the game-listing dialog — it now opens a
  // small popover that lets the user choose between picking another game
  // and picking a genre to compare against.
  const compareButtonRef = useRef<HTMLButtonElement | null>(null)
  const [compareTargetPickerOpen, setCompareTargetPickerOpen] = useState(false)
  const handleCompareClick = useCallback(() => setCompareTargetPickerOpen((prev) => !prev), [])
  const handleCompareTargetPickerClose = useCallback(() => setCompareTargetPickerOpen(false), [])
  const handleCompareTargetPickerSelectGame = useCallback(() => openCompareGame(), [openCompareGame])
  const handleCompareTargetPickerSelectGenre = useCallback((genreId: string) => openCompareGenreResult(genreId), [openCompareGenreResult])

  const gameId = gameIdParam ?? null
  const { data, isLoading, isError, refetch } = useGameCreativeDetails(gameId)
  // True only on the very first fetch (no data yet). We use this to swap
  // the header identity block for an inline loading indicator and to
  // disable the right-side controls — once `data` arrives, subsequent
  // refetches caused by changing the network/month selectors must NOT
  // disable those same selectors (would lock the user out mid-interaction)
  // or hide the title (would flash on every refetch).
  const isInitialLoading = !data && isLoading

  // Always send the user back to the UA Insights dashboard (rather than
  // `navigate(-1)`), so the back action is predictable regardless of how
  // the game page was reached (deep link, refresh, dialog drilldown, etc.).
  // Forward the current `network` and `month` query params so the dashboard
  // re-opens with the same scope the user was viewing here.
  const handleBack = useCallback(() => {
    const next = new URLSearchParams()
    const network = searchParams.get('network')
    const month = searchParams.get('month')
    if (network) next.set('network', network)
    if (month) next.set('month', month)
    const search = next.toString()
    navigate({ pathname: '/ua-insights', search: search ? `?${search}` : '' })
  }, [navigate, searchParams])

  return (
    <Box>
      <UAInsightsPageHeader
        compact={isCompactHeader}
        onBack={handleBack}
        backLabel={t('back_to_ua_insights')}
        disabled={isInitialLoading}
        identity={
          <GamePageIdentity
            grGameId={data?.game.grGameId}
            name={data?.game.name}
            icon={data?.game.icon}
            genre={data?.game.genre}
            compact={isCompactHeader}
            isLoading={isInitialLoading}
          />
        }
        actions={
          <Button
            ref={compareButtonRef}
            variant="contained"
            color="primary"
            startIcon={<CompareArrowsIcon />}
            onClick={handleCompareClick}
            disabled={isInitialLoading}
            aria-haspopup="true"
            aria-expanded={compareTargetPickerOpen}
            sx={{ flexShrink: 0, borderRadius: 999, height: 38 }}
          >
            {t('compare')}
          </Button>
        }
        // Persisted to the URL via the `tab` query param so deep-linking and
        // browser back/forward keep the user on the right section.
        tabs={<GamePageTabs activeTab={activeTab} onTabChange={handleTabChange} compact={isCompactHeader} disabled={isInitialLoading} />}
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {isInitialLoading ? (
          // Body renders a layout-matched skeleton for the active tab while
          // the first fetch is in flight, so the page doesn't look blank
          // between navigation and the first response. The sticky header
          // continues to carry its own inline "Loading game details…"
          // spinner — the two cues complement rather than duplicate each
          // other (header = "what is loading", body = "how it will look").
          <GamePageBodySkeleton activeTab={activeTab} />
        ) : isError && !data ? (
          // A failed request is a different problem from a game we genuinely
          // have no creative data for, and unlike the latter it's worth
          // retrying — so it gets its own branch ahead of the not-found copy.
          <UAInsightsErrorState onRetry={refetch} />
        ) : !data ? (
          <Typography variant="body2" color="text.secondary">
            {t('game_details_not_found')}
          </Typography>
        ) : (
          <>
            {activeTab === 'overview' && <GameOverviewTab gameId={gameId} data={data} />}

            {activeTab === 'motivations' &&
              gameId != null &&
              (activeMotivationId ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <ShowcaseBreadcrumb crumbs={motivationCrumbs} />
                  <GameMotivationDetailView
                    gameId={gameId}
                    motivationId={activeMotivationId}
                    motivationScores={data.motivationScores}
                    onOpenPattern={openPatternFromMotivation}
                  />
                </Box>
              ) : (
                <>
                  <SectionCard title={t('motivation_diagnosis_title')}>
                    <MotivationDiagnosis
                      diagnosis={data.motivationDiagnosis}
                      available={!!data.motivationScores.gameDesign?.length}
                      gameName={data.game.name}
                      gameIcon={data.game.icon}
                    />
                  </SectionCard>

                  <SectionCard title={t('motivation_breakdown_title')}>
                    <MotivationsPanel motivationScores={data.motivationScores} onSelectMotivation={openMotivationView} />
                  </SectionCard>
                </>
              ))}

            {activeTab === 'story-patterns' && <GameStoryPatternsTab gameId={gameId} />}

            {activeTab === 'creative-ads' && data.creativeAds.length > 0 && <CreativeAdsTable rows={data.creativeAds} />}

            {activeTab === 'creative-tags' && <GameCreativeTagsTable tags={data.tags} totalAds={data.creativeAds.length} showAll />}
          </>
        )}
      </Box>

      <UAInsightsDialogs />
      <CompareTargetPicker
        anchorEl={compareButtonRef.current}
        open={compareTargetPickerOpen}
        onClose={handleCompareTargetPickerClose}
        onSelectGameClick={handleCompareTargetPickerSelectGame}
        onSelectGenre={handleCompareTargetPickerSelectGenre}
      />
    </Box>
  )
}

export default UAInsightsGamePage
